import React, { useEffect, useState, useRef, useMemo } from "react";
import HttpService from "../services/httpService";
import { setLoading, setSnackInfo } from "../redux/actions/appAction";
import { connect } from "react-redux";
import { FormControl, TextField } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import MenuItem from "@material-ui/core/MenuItem/MenuItem";
import Autocomplete from "@material-ui/lab/Autocomplete";
import Grid from "@material-ui/core/Grid";
import Tooltip from "@material-ui/core/Tooltip";
import { commaSeparateAmount } from './commaSeparate';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  withStyles,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  TablePagination,
  Radio,
  TableContainer,
  Button,
  CircularProgress,
} from "@material-ui/core";
// Material-UI icons - the Ant Design set these replace was the odd one out
// against every other page in the app, and read as dated beside them.
import DeleteSweepOutlinedIcon from "@material-ui/icons/DeleteSweepOutlined";
import FilterListIcon from "@material-ui/icons/FilterList";
import DescriptionOutlinedIcon from "@material-ui/icons/DescriptionOutlined";
import SearchIcon from "@material-ui/icons/Search";
import ClearAllIcon from "@material-ui/icons/ClearAll";
import InboxOutlinedIcon from "@material-ui/icons/InboxOutlined";
import PhoneOutlinedIcon from "@material-ui/icons/PhoneOutlined";
import RefreshIcon from "@material-ui/icons/Refresh";
import LinearProgress from "@material-ui/core/LinearProgress";
import Chip from "@material-ui/core/Chip";
import Skeleton from "@material-ui/lab/Skeleton";
import { CSVLink } from "react-csv";
import moment from "moment";
import { ROLENAME } from "../utils/index";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";

// Brand orange (sampled from the Decorpot logo mark) - replaces the navy/blue
// this page used to be themed in.
const ORANGE = "#F47E44";
const ORANGE_DARK = "#c1531f";

// Warm, on-brand avatar colours, picked by name hash so a given customer keeps
// the same colour between renders and pages.
const AVATAR_COLORS = [
  { bg: "#fff1ea", fg: "#c1531f" }, { bg: "#fff4d6", fg: "#b8860b" },
  { bg: "#e8f6ef", fg: "#1e8a5f" }, { bg: "#eaf1ff", fg: "#2c5fb8" },
  { bg: "#f5ecff", fg: "#7b3fd6" }, { bg: "#ffeaf1", fg: "#c1315f" },
];
const avatarStyle = (name = "") => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  const c = AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
  return { backgroundColor: c.bg, color: c.fg };
};

// Indeterminate bar shown while the table is being fetched. Same treatment as
// the other orange admin pages, so they read as one system.
const BrandLinearProgress = withStyles(() => ({
  root: { height: 4, borderRadius: 999, backgroundColor: "rgba(193,83,31,0.10)" },
  bar: { borderRadius: 999, background: `linear-gradient(90deg, ${ORANGE_DARK}, ${ORANGE}, #ff9c66)` },
}))(LinearProgress);

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  pageRoot: {
    padding: "4px 4px 24px",
  },
  headerBanner: {
    background: `linear-gradient(135deg, ${ORANGE_DARK} 0%, ${ORANGE} 60%, #ff9c66 100%)`,
    borderRadius: 16,
    padding: "22px 28px",
    display: "flex",
    alignItems: "center",
    gap: 18,
    marginBottom: 20,
    color: "#fff",
    boxShadow: "0 8px 24px rgba(217,101,44,0.3)",
    flexWrap: "wrap",
  },
  headerIconWrap: {
    width: 56,
    height: 56,
    borderRadius: 16,
    background: "rgba(255,255,255,0.2)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  headerTitle: {
    fontSize: 22,
    fontWeight: 700,
    margin: 0,
    color: "#fff",
  },
  headerSubtitle: {
    fontSize: 13,
    color: "rgba(255,255,255,0.85)",
    marginTop: 4,
  },
  headerActions: {
    marginLeft: "auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 10,
    flexWrap: "wrap",
  },
  refreshBtn: {
    background: "#fff",
    color: ORANGE_DARK,
    fontWeight: 700,
    borderRadius: 10,
    textTransform: "none",
    padding: "8px 18px",
    boxShadow: "0 4px 14px rgba(0,0,0,0.15)",
    "&:hover": { background: "#fff1ea" },
    "&.Mui-disabled": { background: "rgba(255,255,255,0.8)", color: ORANGE_DARK, boxShadow: "none" },
  },
  spin: { animation: "$spin 0.9s linear infinite" },
  "@keyframes spin": { from: { transform: "rotate(0deg)" }, to: { transform: "rotate(360deg)" } },
  lastUpdatedText: {
    width: "100%", display: "flex", alignItems: "center", justifyContent: "flex-end",
    gap: 6, fontSize: 12, fontWeight: 600, color: "rgba(255,255,255,0.92)",
  },
  liveDot: {
    width: 7, height: 7, borderRadius: "50%",
    background: "#8ef5b5", boxShadow: "0 0 0 3px rgba(142,245,181,0.25)",
  },
  progressSlot: { height: 4, marginBottom: 10 },
  loadingBanner: {
    display: "flex", alignItems: "center", gap: 10,
    padding: "9px 14px", marginBottom: 14, borderRadius: 12,
    background: "#fff1ea", border: `1px solid ${ORANGE}33`,
  },
  loadingBannerTitle: { fontSize: 13, fontWeight: 700, color: ORANGE_DARK },
  loadingBannerHint: { fontSize: 11.5, color: "#7a6a60", marginTop: 1 },
  customerCell: { display: "flex", alignItems: "center", gap: 8, minWidth: 0 },
  avatarChip: {
    width: 26, height: 26, borderRadius: "50%", flexShrink: 0,
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    fontSize: 11, fontWeight: 800,
  },
  truncate: { minWidth: 0, maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "inline-block" },
  truncateWide: { minWidth: 0, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "inline-block", verticalAlign: "middle" },
  stageChip: { height: 22, fontSize: 11, fontWeight: 700, background: "#eef2fa", color: "#2c5fb8", maxWidth: 170 },
  leadTypeChip: { height: 22, fontSize: 11, fontWeight: 800, background: "#eef2fa", color: "#2c5fb8", border: "1px solid #d6e0f5" },
  monoCell: { fontVariantNumeric: "tabular-nums", letterSpacing: 0.2 },
  amountCell: { fontWeight: 700, color: "#1e7e34", fontVariantNumeric: "tabular-nums" },
  naText: { color: "#c3cad8", fontWeight: 600 },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    flexWrap: "wrap",
    minWidth: 0,
  },
  headerStatsChip: {
    background: "rgba(255,255,255,0.2)",
    borderRadius: 20,
    padding: "8px 18px",
    fontSize: 13,
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  filterCard: {
    padding: "22px 26px",
    borderRadius: 16,
    marginBottom: 20,
    border: "1px solid #eceff3",
    background: "#fff",
    boxShadow: "0 2px 12px rgba(16,24,40,0.05)",
  },
  sectionLabel: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 12.5,
    fontWeight: 700,
    color: "#5a6b85",
    textTransform: "uppercase",
    letterSpacing: 0.6,
    marginBottom: 14,
  },
  filterDivider: {
    margin: "22px 0",
    border: "none",
    borderTop: "1px dashed #e0e5ee",
  },
  roundedField: {
    width: "100%",
    "& .MuiOutlinedInput-root": {
      borderRadius: 12,
      backgroundColor: "#fafbfd",
    },
  },
  actionBtnBase: {
    borderRadius: 24,
    fontWeight: 600,
    height: 40,
    width: "100%",
    textTransform: "none",
    boxShadow: "none",
    cursor: "pointer",
    transition: "transform 150ms ease, box-shadow 150ms ease",
    "&:hover:not(.Mui-disabled)": {
      transform: "translateY(-1px)",
    },
    "&.Mui-disabled": {
      cursor: "not-allowed",
      background: "rgba(0, 0, 0, 0.12)",
      color: "rgba(0, 0, 0, 0.38)",
    },
  },
  submitBtn: {
    background: `linear-gradient(135deg, ${ORANGE_DARK} 0%, ${ORANGE} 100%)`,
    color: "#fff",
    boxShadow: "0 4px 12px rgba(244,126,68,0.35)",
    "&:hover:not(.Mui-disabled)": {
      background: `linear-gradient(135deg, #a8431a 0%, ${ORANGE_DARK} 100%)`,
      boxShadow: "0 6px 16px rgba(244,126,68,0.45)",
    },
  },
  downloadBtn: {
    background: "linear-gradient(135deg, #27ae60 0%, #219150 100%)",
    color: "#fff",
    boxShadow: "0 4px 12px rgba(39,174,96,0.3)",
    "&:hover:not(.Mui-disabled)": {
      background: "linear-gradient(135deg, #219150 0%, #1b7a43 100%)",
      boxShadow: "0 6px 16px rgba(39,174,96,0.4)",
    },
  },
  searchBtn: {
    background: ORANGE,
    color: "#fff",
    boxShadow: "0 4px 12px rgba(244,126,68,0.3)",
    "&:hover:not(.Mui-disabled)": {
      background: ORANGE_DARK,
      boxShadow: "0 6px 16px rgba(244,126,68,0.4)",
    },
  },
  clearBtn: {
    color: "#8a94a6",
    borderColor: "#e0e5ee",
    "&:hover:not(.Mui-disabled)": {
      borderColor: "#c7cdd9",
      background: "rgba(0,0,0,0.03)",
    },
  },
  tableCard: {
    borderRadius: 16,
    overflow: "hidden",
    border: "1px solid #eceff3",
    boxShadow: "0 2px 12px rgba(16,24,40,0.05)",
  },
  tableTopBar: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    borderBottom: "1px solid #eceff3",
  },
  tableScrollArea: {
    maxHeight: 700,
    overflow: "auto",
  },
  table: {
    minWidth: 900,
  },
  tableHeaderCell: {
    textAlign: "center",
    fontWeight: 700,
    background: ORANGE_DARK,
    color: "#fff",
    fontSize: 12.5,
    letterSpacing: 0.3,
    whiteSpace: "nowrap",
  },
  tableBodyCell: {
    textAlign: "center",
    fontSize: 13,
  },
  tableRow: {
    "&:nth-of-type(even)": {
      backgroundColor: "#fafbfd",
    },
    "&:hover": {
      backgroundColor: "#fdf1ea",
    },
  },
  leadNoLink: {
    color: ORANGE_DARK,
    fontWeight: 600,
    textDecoration: "none",
    cursor: "pointer",
    "&:hover": { textDecoration: "underline", color: ORANGE },
  },
  emptyStateWrap: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "50px 20px",
    color: "#8a94a6",
  },
  loadingStateWrap: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "50px 20px",
    color: "#8a94a6",
    gap: 12,
  },
}));

const MarketingToBeCalled = (props) => {
  const classes = useStyles();
  const { userDetails } = useSelector((state) => state.user);
  const [storeDateFilter, setPivotDatefilters] = useState({
    startDate: "",
    endDate: "",
  });
  const [searchInput, setSearchInput] = useState("");
  const [selectedSearchType, setSelectedSearchType] = useState("");
  const [selectedFilterOption, setSelectedFilterOption] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [data, setData] = useState([]);
  const [csvData, setCsvData] = useState([]);
  const buttonRef = useRef(null);
  const [leadStatusData, setLeadStatusData] = useState([])
  // Scoped loaders - drives the button spinners and the in-table loading
  // state below, instead of the app-wide Backdrop overlay this page used to
  // rely on for these two actions.
  const [dataLoading, setDataLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  // When the rows on screen were last pulled. Only set on a SUCCESSFUL fetch,
  // so a failed refresh never makes stale rows look fresh.
  const [lastUpdated, setLastUpdated] = useState(null);
  // "Updated 4 minutes ago" is only true at the instant it renders, so a slow
  // tick re-renders it. 30s is plenty - moment rounds to the minute anyway.
  const [nowTick, setNowTick] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);
  const searchTypes = [
    "Customer Name",
    "Email ID",
    "Lead No",
    "Project No",
    "Contact No",
    "Unique Lead No",
  ];

  const headers = [
    { key: "serialNo", label: "Sl. No." },
    { key: "_id", label: "Lead Id" },
    { key: "currentStage", label: "Current Stage" },
    { key: "leadType", label: "Lead Type" },
    { key: "leadNumber", label: "Lead No." },
    { key: "leadCreatedDate", label: "Lead Created Date" },
    { key: "projectCode", label: "Unique Lead No." },
    { key: "clientName", label: "Client Name" },
    // { key: "clientEmail", label: "Client Email" },
    // { key: "clientNumber", label: "Client Number" },
    { key: "department", label: "Department" },
    { key: "sourceOfLead", label: "Source Of Lead" },
    { key: "expCtrName", label: "Exp. Ctr Name" },
    { key: "cityName", label: "City Name" },
    { key: "salesExecutive", label: "Sales Executive" },
    { key: "asm", label: "ASM" },
    { key: "projectNeeds", label: "SOW" },
    { key: "threeLakhsSuits", label: "Three Lakh Suits" },
    { key: "startProjectBy", label: "Start Project By" },
  ];

  const csvDownloadHandler = async (data) => {
    setDownloadLoading(true);
    try {
      let csvArray = [];
      for (let i = 0; i < data.length; i++) {
        let obj = {
          serialNo: i + 1,
          _id: data[i]?._id || "NA",
          projectCode: data[i]?.uniqueLeadNumber || "N/A",
          leadNumber: data[i]?.lead_no || "N/A",
          clientName: data[i]?.customerId?.name || "N/A",
          // clientEmail: data[i]?.customerId?.email || "N/A",
          // clientNumber: data[i]?.customerId?.contact_no || "N/A",
          expCtrName: data[i]?.experienceCenterId?.name || "N/A",
          cityName: data[i]?.city?.name || "N/A",
          currentStage: data[i]?.currentStage || "NA",
          leadType: data[i]?.leadType || "NA",
          department: data[i]?.departmentId?.name || "NA",
          sourceOfLead: data[i]?.sourceOfLead?.name || "NA",
          leadCreatedDate: moment(data[i]?.createdAt).format('DD-MM-YYYY') || "NA",
          salesExecutive: data[i]?.salesUser?.name || "NA",
          asm: data[i]?.salesAssistantManager?.name || "NA",
          projectNeeds: data[i]?.projectNeeds || "NA",
          threeLakhsSuits: data[i]?.threeLakhsSuits || "NA",
          startProjectBy: data[i]?.startProjectBy || "NA",
        };

        csvArray.push(obj);
      }
      setCsvData(csvArray);
      downloadExcel();
    } catch (error) {
      console.log("error", error);
      props?.setSnackInfo(
        error?.response?.data?.message ?? "Something went wrong",
        "error"
      );
    }
    setDownloadLoading(false);
  };

  const downloadExcel = () => {
    try {
      setTimeout(() => {
        buttonRef.current.link.click();
      });
    } catch (err) {
      console.error(err.message);
    }
  }

  useEffect(() => {
    getToBeCalledMarketingData();
  }, []);

  const storePivotDate = (e) => {
    const { name, value } = e.target;
    setPivotDatefilters({ ...storeDateFilter, [name]: value });
  };

  //handle search ribbon
  const handleSearchType = (value) => {
    setSelectedSearchType(value);
    setSearchInput("");
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && selectedSearchType && searchInput) {
      handleSearch(searchInput.toUpperCase());
    }
  };

  const handleSearch = async (value) => {
    getToBeCalledMarketingData(
      selectedSearchType,
      searchInput
    );
  };

  // handle submit button
  const handleSubmitButton = () => {
    getToBeCalledMarketingData();
  };

  //clear filter function
  const clearDateFilter = () => {
    setSearchInput("");
    setSelectedSearchType("");
    setPivotDatefilters({
      startDate: "",
      endDate: "",
    });
    getToBeCalledMarketingData();
  };


  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  // getToBeCalledMarketingData main data function
  // Newest first, by TIMESTAMP. The previous comparator did
  //   b.createdAt.split("-").reverse().join().localeCompare(...)
  // which on an ISO date ("2026-10-07T10:33:00.000Z") yields
  // "07T10:33:00.000Z,10,2026" - so it sorted by day-of-month, then month,
  // and threw outright on a lead with no createdAt.
  const sortedData = useMemo(() => {
    const rows = Array.isArray(data) ? [...data] : [];
    return rows.sort((a, b) => {
      const at = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bt = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
      return bt - at;
    });
  }, [data]);

  const getToBeCalledMarketingData = async (selectedSearchType, searchInput) => {
    setDataLoading(true);
    try {
      let obj = {
        startDate: storeDateFilter.startDate,
        endDate: storeDateFilter.endDate,
        dateFilterType: selectedFilterOption,
        experienceCenterId: userDetails.experienceCenterId,
        searchType: selectedSearchType ? selectedSearchType : "",
        search: searchInput ? searchInput.toUpperCase() : "",
      }
      let response = await HttpService.getToBeCalledMarketingData(obj);
      setData(response?.data);
      setLeadStatusData(response.data)
      // Inside the try, after the assignment: a fetch that threw leaves the
      // previous timestamp alone rather than vouching for stale data.
      setLastUpdated(moment());
    }
    catch (err) {
      props.setSnackInfo('Something went wrong', 'error');
      console.error(err);
    }
    setDataLoading(false)
  }

  return (
    <div className={classes.pageRoot}>
      {/* Header */}
      <div className={classes.headerBanner}>
        {/* Icon, title and the record count are ONE group on the left. They used
            to be siblings of the actions block with BOTH the chip and the
            actions carrying marginLeft:auto - two auto margins in one flex row,
            so the chip was pushed off the title and left stranded mid-banner. */}
        <div className={classes.headerLeft}>
          <div className={classes.headerIconWrap}>
            <PhoneOutlinedIcon style={{ fontSize: 26, color: "#fff" }} />
          </div>
          <div>
            <h3 className={classes.headerTitle}>Lead Type - To Be Called</h3>
            <div className={classes.headerSubtitle}>
              Leads that still need a follow-up call.
            </div>
          </div>
          <div className={classes.headerStatsChip}>
            <PhoneOutlinedIcon style={{ fontSize: 17 }} />
            {leadStatusData?.length || 0} Records
          </div>
        </div>

        <div className={classes.headerActions}>
          <Tooltip
            title={
              lastUpdated
                ? `Last loaded at ${lastUpdated.format("DD MMM YYYY, hh:mm:ss A")} — click to reload`
                : "Reload the To Be Called leads"
            }
          >
            {/* A disabled button fires no mouse events, so the tooltip would die
                exactly when it is most wanted - mid-refresh. */}
            <span style={{ display: "inline-flex" }}>
              <Button
                className={classes.refreshBtn}
                disabled={dataLoading}
                onClick={() => getToBeCalledMarketingData(selectedSearchType, searchInput)}
                startIcon={<RefreshIcon className={dataLoading ? classes.spin : ""} />}
              >
                {dataLoading ? "Refreshing…" : "Refresh"}
              </Button>
            </span>
          </Tooltip>
          {lastUpdated && (
            <div className={classes.lastUpdatedText}>
              <span className={classes.liveDot} />
              {dataLoading ? "Loading…" : `Updated ${moment(lastUpdated).from(nowTick)}`}
            </div>
          )}
        </div>
      </div>

      {/* Reserved slot rather than a conditional element, so starting a fetch
          does not jog the page down by 4px and back again. */}
      <div className={classes.progressSlot}>
        {dataLoading ? <BrandLinearProgress /> : null}
      </div>

      {/* The bar alone says "something is happening"; this says WHAT, which
          matters most on a refresh. */}
      {dataLoading && (
        <div className={classes.loadingBanner}>
          <CircularProgress size={17} thickness={5} style={{ color: ORANGE_DARK }} />
          <div style={{ minWidth: 0 }}>
            <div className={classes.loadingBannerTitle}>Fetching To Be Called leads…</div>
            <div className={classes.loadingBannerHint}>
              {data?.length
                ? "Reloading for the current filters — the leads below stay visible until the new data lands."
                : "Pulling every lead awaiting a follow-up call for the selected range and filters."}
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <Paper elevation={0} className={classes.filterCard}>
        <div className={classes.sectionLabel}>
          <FilterListIcon /> Filters
        </div>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              id="startDate"
              name="startDate"
              label="From Created Date"
              type="date"
              variant="outlined"
              size="small"
              value={storeDateFilter.startDate}
              onChange={storePivotDate}
              InputLabelProps={{ shrink: true }}
              className={classes.roundedField}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              id="endDate"
              name="endDate"
              label="End Created Date"
              type="date"
              variant="outlined"
              size="small"
              value={storeDateFilter.endDate}
              onChange={storePivotDate}
              InputLabelProps={{ shrink: true }}
              className={classes.roundedField}
              fullWidth
            />
          </Grid>
          <Grid item xs={6} sm={3} md={3}>
            <Button
              variant="contained"
              disabled={dataLoading}
              startIcon={dataLoading ? <CircularProgress size={16} style={{ color: "#fff" }} /> : <FilterListIcon />}
              onClick={handleSubmitButton}
              className={`${classes.actionBtnBase} ${classes.submitBtn}`}
            >
              {dataLoading ? "Loading..." : "Submit"}
            </Button>
          </Grid>
          <Grid item xs={6} sm={3} md={3}>
            <Button
              variant="contained"
              disabled={downloadLoading || !data?.length}
              startIcon={downloadLoading ? <CircularProgress size={16} style={{ color: "#fff" }} /> : <DescriptionOutlinedIcon />}
              onClick={() => csvDownloadHandler(data)}
              className={`${classes.actionBtnBase} ${classes.downloadBtn}`}
            >
              {downloadLoading ? "Preparing..." : "Download Excel"}
            </Button>
            <CSVLink
              headers={headers}
              filename={'To-Be-Called.csv'}
              data={csvData}
              ref={buttonRef}
              style={{ display: "none" }}
            />
          </Grid>
        </Grid>

        <hr className={classes.filterDivider} />

        <div className={classes.sectionLabel}>
          <SearchIcon /> Search
        </div>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4} md={3}>
            <TextField
              select
              id="demo-simple-select"
              value={selectedSearchType}
              label="Search Type"
              name="searchType"
              onChange={(e) => handleSearchType(e.target.value)}
              variant="outlined"
              size="small"
              className={classes.roundedField}
              fullWidth
            >
              {searchTypes &&
                searchTypes?.map((el, index) => (
                  <MenuItem key={index} value={el}>
                    {el}
                  </MenuItem>
                ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={5} md={6}>
            <TextField
              label="Enter the input"
              name="searched"
              size="small"
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value);
              }}
              onKeyDown={handleKeyDown}
              variant="outlined"
              className={classes.roundedField}
              fullWidth
            />
          </Grid>

          <Grid item xs={6} sm={2} md={1}>
            <Tooltip title={!selectedSearchType || !searchInput ? "Select a search type and enter a value" : ""}>
              <span>
                <Button
                  variant="contained"
                  disabled={!selectedSearchType || !searchInput || dataLoading}
                  startIcon={dataLoading ? <CircularProgress size={16} style={{ color: "#fff" }} /> : <SearchIcon />}
                  className={`${classes.actionBtnBase} ${classes.searchBtn}`}
                  onClick={() => handleSearch(searchInput.toUpperCase())}
                >
                  Search
                </Button>
              </span>
            </Tooltip>
          </Grid>

          <Grid item xs={6} sm={1} md={2}>
            <Button
              variant="outlined"
              disabled={dataLoading}
              startIcon={<ClearAllIcon />}
              className={`${classes.actionBtnBase} ${classes.clearBtn}`}
              onClick={clearDateFilter}
            >
              Clear
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <Paper elevation={0} className={classes.tableCard}>
        <div className={classes.tableTopBar}>
          <TablePagination
            rowsPerPageOptions={[20, 50, 100, 200, 500, { label: 'All', value: leadStatusData?.length }]}
            colSpan={13}
            count={leadStatusData?.length || 0}
            rowsPerPage={rowsPerPage}
            page={page}
            SelectProps={{
              inputProps: { 'aria-label': 'rows per page' },
              native: true,
            }}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            style={{ flexShrink: 0 }}
          />
        </div>

        <TableContainer className={classes.tableScrollArea}>
          <Table
            className={classes.table}
            stickyHeader
            aria-label="sticky table"
          >
            <TableHead>
              <TableRow>
                <TableCell className={classes.tableHeaderCell}> Sl No. </TableCell>
                <TableCell className={classes.tableHeaderCell}> Customer </TableCell>
                <TableCell className={classes.tableHeaderCell}> Lead No </TableCell>
                <TableCell className={classes.tableHeaderCell}> Unique Lead No. </TableCell>
                <TableCell className={classes.tableHeaderCell}> Current Stage </TableCell>
                <TableCell className={classes.tableHeaderCell}> Mobile </TableCell>
                <TableCell className={classes.tableHeaderCell}> Address  </TableCell>
                <TableCell className={classes.tableHeaderCell}> Area </TableCell>
                <TableCell className={classes.tableHeaderCell}> Lead Type </TableCell>
                <TableCell className={classes.tableHeaderCell}> Assigned to </TableCell>
                <TableCell className={classes.tableHeaderCell}> Quotation Amount </TableCell>
                <TableCell className={classes.tableHeaderCell}> Created At </TableCell>
                <TableCell className={classes.tableHeaderCell}> Expected Closure Date </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {dataLoading && !data?.length ? (
                // Placeholder rows shaped like real ones, so the table keeps its
                // height and column rhythm instead of collapsing to a spinner and
                // snapping back. A REFRESH (rows already present) keeps them on
                // screen rather than blanking the table.
                Array.from({ length: 8 }).map((_, i) => (
                  <TableRow key={`sk-${i}`}>
                    <TableCell className={classes.tableBodyCell}><Skeleton variant="text" width={18} /></TableCell>
                    <TableCell className={classes.tableBodyCell}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <Skeleton variant="circle" width={26} height={26} />
                        <Skeleton variant="text" width={110} />
                      </div>
                    </TableCell>
                    <TableCell className={classes.tableBodyCell}><Skeleton variant="text" width={70} /></TableCell>
                    <TableCell className={classes.tableBodyCell}><Skeleton variant="text" width={90} /></TableCell>
                    <TableCell className={classes.tableBodyCell}><Skeleton variant="rect" width={92} height={22} style={{ borderRadius: 999 }} /></TableCell>
                    <TableCell className={classes.tableBodyCell}><Skeleton variant="text" width={92} /></TableCell>
                    <TableCell className={classes.tableBodyCell}><Skeleton variant="text" width={140} /></TableCell>
                    <TableCell className={classes.tableBodyCell}><Skeleton variant="text" width={50} /></TableCell>
                    <TableCell className={classes.tableBodyCell}><Skeleton variant="rect" width={54} height={22} style={{ borderRadius: 999 }} /></TableCell>
                    <TableCell className={classes.tableBodyCell}><Skeleton variant="text" width={96} /></TableCell>
                    <TableCell className={classes.tableBodyCell}><Skeleton variant="text" width={82} /></TableCell>
                    <TableCell className={classes.tableBodyCell}><Skeleton variant="text" width={80} /></TableCell>
                    <TableCell className={classes.tableBodyCell}><Skeleton variant="text" width={80} /></TableCell>
                  </TableRow>
                ))
              ) : data?.length !== 0 ? (
                // sortedData is sorted ONCE over the whole set, then sliced. This used to
                // slice first and sort after, so each page was sorted only within itself.
                sortedData
                  .slice(
                    page * rowsPerPage,
                    page * rowsPerPage + rowsPerPage
                  )
                  .map((row, i) => {
                    return (
                      <TableRow key={i + 1} className={classes.tableRow}>
                        <TableCell className={classes.tableBodyCell}>{page * rowsPerPage + i + 1}</TableCell>
                        <TableCell className={classes.tableBodyCell} component="th" scope="row">
                          <div className={classes.customerCell}>
                            <span className={classes.avatarChip} style={avatarStyle(row?.customerId?.name)}>
                              {row?.customerId?.name?.charAt(0)?.toUpperCase() || "?"}
                            </span>
                            <Tooltip title={row?.customerId?.name || ""}>
                              <span className={classes.truncate}>{row?.customerId?.name || "-"}</span>
                            </Tooltip>
                          </div>
                        </TableCell>
                        <TableCell className={classes.tableBodyCell} component="th">
                          <Link
                            target="_blank"
                            to={`/project-logs/${row?._id}`}
                            className={classes.leadNoLink}
                          >
                            {row?.lead_no}
                          </Link>
                        </TableCell>
                        <TableCell className={classes.tableBodyCell} component="th"> {row?.uniqueLeadNumber ? row?.uniqueLeadNumber : '-'} </TableCell>
                        <TableCell className={classes.tableBodyCell} component="th">
                          {row?.currentStage
                            ? <Chip size="small" className={classes.stageChip} label={row.currentStage} />
                            : <span className={classes.naText}>-</span>}
                        </TableCell>
                        <TableCell className={classes.tableBodyCell} component="th">
                          <span className={classes.monoCell}>{row?.customerId?.contact_no || "-"}</span>
                        </TableCell>
                        <TableCell className={classes.tableBodyCell} component="th">
                          {/* Address is the one free-text column here, so it is
                              the only one that can stretch a row. */}
                          <Tooltip title={row?.customerId?.address || ""}>
                            <span className={classes.truncateWide}>{row?.customerId?.address || "-"}</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell className={classes.tableBodyCell} component="th"> {row?.area} </TableCell>
                        <TableCell className={classes.tableBodyCell} component="th">
                          {row?.leadType
                            ? <Chip size="small" className={classes.leadTypeChip} label={row.leadType} />
                            : <span className={classes.naText}>-</span>}
                        </TableCell>
                        <TableCell className={classes.tableBodyCell} component="th">
                          <Tooltip title={row?.assignTo?.name || ""}>
                            <span className={classes.truncate}>{row?.assignTo?.name || "-"}</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell className={classes.tableBodyCell} component="th">
                          <span className={row?.grandTotal ? classes.amountCell : classes.naText}>
                            ₹ {row?.grandTotal ? commaSeparateAmount(row.grandTotal) : '0.00'}
                          </span>
                        </TableCell>
                        <TableCell className={classes.tableBodyCell} component="th"> {row?.createdAt ? moment(row?.createdAt).format('DD-MM-YYYY') : 'NA'} </TableCell>
                        <TableCell className={classes.tableBodyCell} component="th"> {row?.expectedSalesclosureDate ? moment(row?.expectedSalesclosureDate).format('DD-MM-YYYY') : 'NA'} </TableCell>
                      </TableRow>
                    );
                  })
              ) : (
                <TableRow>
                  <TableCell colSpan={13} style={{ border: "none" }}>
                    <div className={classes.emptyStateWrap}>
                      <PhoneOutlinedIcon style={{ fontSize: 42, marginBottom: 10 }} />
                      <div style={{ fontWeight: 600 }}>No To Be Called Leads Found</div>
                      <div style={{ fontSize: 12.5, marginTop: 4 }}>Try adjusting the filters or date range above</div>
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </div>
  );
};

export default connect(null, { setLoading, setSnackInfo })(MarketingToBeCalled);
